// @rpg-unit/camera-profile-presets: camera profile presets (tier 3; composition framework, decision 0077). Eleven
// tuned framings on @rpg/camera's profile model, cut from spike 002's `content/tables/camera-profiles.json`: explore,
// combat, lock-on, dialogue, menu, interior, photo, climb, swim, mounted and the editor's. Each row is every number
// @rpg/camera's `PROFILE_KEYS` names (distance, pitch range, fov, pivot, follow rates, look-ahead, drift back, focus,
// blend time) plus the layer a game requests it on (mode, context, overlay) and the reason for its numbers.
//
// A game takes the presets three ways:
//   - as content: list this package in its content folder's `include.json` (@rpg/content reads `src/content` from
//     here) or add `CAMERA_PROFILE_CONTENT` to the tables it loads in the browser; the rows are the
//     `camera-profiles` table;
//   - as @rpg/camera profiles: `presetProfiles()` gives the profiles by name and their layers, ready for a
//     `ProfileStack` (`profileStack()` makes one on the three layers);
//   - tuned by perceptual knobs: `tuneProfiles(rows, { distance, snap, fov })`.
import { ProfileStack, type CameraProfile } from "@rpg/camera";
import table from "./content/tables/camera-profiles.json";
import schema from "./content/schemas/camera-profiles.schema.json";

/** This unit's version (the manifest's and package.json's). */
export const CAMERA_PROFILE_PRESETS_VERSION = "0.1.0" as const;

/** The layers a profile is requested on, lowest first: the controller's mode, a context (combat, a room), an overlay
 *  (dialogue, a menu). A higher layer's request wins while it lasts. */
export const PROFILE_LAYERS = ["mode", "context", "overlay"] as const;
export type ProfileLayer = (typeof PROFILE_LAYERS)[number];

/** One preset, as its schema describes it (`src/content/schemas/camera-profiles.schema.json`): an @rpg/camera
 *  profile with its layer and the reason for its numbers. */
export interface CameraProfilePreset extends CameraProfile {
  id: string;
  layer: ProfileLayer;
  reason: string;
}

/** The presets as a content table (`{ table: "camera-profiles", version, rows }`). */
export const CAMERA_PROFILES_TABLE = table as { table: "camera-profiles"; version: number; rows: CameraProfilePreset[] };

/** The table's row schema (JSON Schema). */
export const CAMERA_PROFILES_SCHEMA = schema as Record<string, unknown>;

/** The content this unit adds to a game, in @rpg/content's shape: its tables and their schemas by table name. */
export const CAMERA_PROFILE_CONTENT = {
  package: "@rpg-unit/camera-profile-presets",
  version: CAMERA_PROFILE_PRESETS_VERSION,
  tables: [CAMERA_PROFILES_TABLE],
  schemas: { "camera-profiles": CAMERA_PROFILES_SCHEMA },
} as const;

/** A preset as an @rpg/camera profile (its numbers only). */
export function toProfile(row: CameraProfilePreset): CameraProfile {
  const { id: _id, layer: _layer, reason: _reason, ...p } = row;
  return p;
}

/** The presets as @rpg/camera profiles by name, and each one's layer. */
export function presetProfiles(rows: readonly CameraProfilePreset[] = CAMERA_PROFILES_TABLE.rows): { profiles: Record<string, CameraProfile>; layers: Record<string, ProfileLayer> } {
  const profiles: Record<string, CameraProfile> = {};
  const layers: Record<string, ProfileLayer> = {};
  for (const r of rows) {
    profiles[r.id] = toProfile(r);
    layers[r.id] = r.layer;
  }
  return { profiles, layers };
}

/** An @rpg/camera profile stack on the presets and the three layers, starting at `initial` (default "explore"). */
export function profileStack(rows: readonly CameraProfilePreset[] = CAMERA_PROFILES_TABLE.rows, initial = "explore"): ProfileStack {
  return new ProfileStack(presetProfiles(rows).profiles, { initial, layers: PROFILE_LAYERS });
}

/** The perceptual knobs (rule 13): what a person would say, and what each changes. */
export const CAMERA_PROFILE_KNOBS = {
  distance: "closer or farther: scales every profile's arm length (1 as tuned; 0.8 a fifth closer). The schema's range still holds.",
  snap: "snappier or lazier: scales the follow rates (horizontal, vertical, in the air) and the look-ahead's rate, and shortens the blends by the same factor (2 follows twice as tightly and blends in half the time).",
  fov: "wider or narrower: degrees added to every profile's field of view (0 as tuned; +5 a little wider).",
} as const;

/** Knob values; `distance` and `snap` default to 1, `fov` to 0 (as tuned). */
export interface CameraKnobs {
  distance?: number;
  snap?: number;
  fov?: number;
}

const round = (x: number): number => Math.round(x * 1e6) / 1e6;
const clamp = (x: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, x));

/** The presets turned by the knobs (new rows; the input is untouched). With no knobs, or all as tuned, equal to the
 *  input. Results stay inside the schema's ranges (distance 0.5..20 m, fov 20..100°, rates 0..60/s). */
export function tuneProfiles(rows: readonly CameraProfilePreset[], knobs: CameraKnobs = {}): CameraProfilePreset[] {
  const distance = knobs.distance ?? 1;
  const snap = knobs.snap ?? 1;
  const fov = knobs.fov ?? 0;
  if (!(distance > 0) || !(snap > 0) || !Number.isFinite(fov)) throw new Error(`camera presets: distance and snap must be > 0 and fov finite (got ${distance}, ${snap}, ${fov})`);
  return rows.map((r) => {
    const out: CameraProfilePreset = structuredClone(r);
    if (distance !== 1) out.distance = round(clamp(r.distance * distance, 0.5, 20));
    if (snap !== 1) {
      out.followH = round(clamp(r.followH * snap, 0, 60));
      out.followV = round(clamp(r.followV * snap, 0, 60));
      out.followVAir = round(clamp(r.followVAir * snap, 0, 60));
      out.leadRate = round(clamp(r.leadRate * snap, 0, 30));
      out.blendIn = round(r.blendIn / snap);
    }
    if (fov !== 0) out.fov = round(clamp(r.fov + fov, 20, 100));
    return out;
  });
}
